import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { X, Pencil, Loader2 } from "lucide-react";
import heartLoading from '../../../../assests/heartLoading.gif';


const TABS = [
  { id: "details", label: "Product Info" },
  { id: "info", label: "Asset info" },
];

const EditProductModal = ({ open, onClose, productId, initialName = "", product, onSuccess, onFailed }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [hierarchy, setHierarchy] = useState([]);
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);
  const [form, setForm] = useState({ productName: initialName, parentDomainId: "", domainId: "", subDomainId: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [originalAssets, setOriginalAssets] = useState([]);
  const [savingAssets, setSavingAssets] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [searchDesc, setSearchDesc] = useState("");

  const [colWidth, setColWidth] = useState(42);
  const tableWrapperRef = useRef(null);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onDragMouseDown = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    startX.current = e.clientX;
    startW.current = colWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [colWidth]);

  const onMouseMove = useCallback((e) => {
    if (!isResizing.current || !tableWrapperRef.current) return;
    const wrapperWidth = tableWrapperRef.current.offsetWidth;
    const delta = e.clientX - startX.current;
    const newPct = Math.min(75, Math.max(20, startW.current + (delta / wrapperWidth) * 100));
    setColWidth(newPct);
  }, []);

  const onMouseUp = useCallback(() => {
    if (isResizing.current) {
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  useEffect(() => {
    if (open) {
      setActiveTab("details");
      setEditMode(false);
      setSaveSuccess(false);
      setSearchName("");
      setSearchDesc("");
      setForm({
        productName: initialName,
        parentDomainId: product?.parentDomainId,
        domainId: product?.domainId,
        subDomainId: product?.subDomainId,
      });
      setErrors({});
    }
  }, [open, initialName, productId]);

  useEffect(() => {
    let isMounted = true;
    const fetchHierarchy = async () => {
      setLoadingHierarchy(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/parentdomain_domain_subdomain_list/`);
        if (isMounted) setHierarchy(response.data?.parent_domains || []);
      } catch (error) {
        console.error("Failed to load domain hierarchy", error);
      } finally {
        if (isMounted) setLoadingHierarchy(false);
      }
    };
    fetchHierarchy();
    return () => { isMounted = false; };
  }, []);

  const parentOptions = useMemo(
    () => hierarchy.map((p) => ({ id: p.parent_domain_id, label: p.parent_domain, domains: p.domains || [] })),
    [hierarchy]
  );

  const domainOptions = useMemo(() => {
    const all = parentOptions.flatMap((p) =>
      p.domains.map((d) => ({ id: d.domain_id, label: d.domain, parentId: p.id, subDomains: d.sub_domains || [] }))
    );
    return form.parentDomainId ? all.filter((d) => d.parentId === form.parentDomainId) : all;
  }, [parentOptions, form.parentDomainId]);

  const subDomainOptions = useMemo(() => {
    const all = domainOptions.flatMap((d) =>
      d.subDomains.map((s) => ({ id: s.sub_domain_id, label: s.sub_domain, domainId: d.id, parentId: d.parentId }))
    );
    if (form.domainId) return all.filter((s) => s.domainId === form.domainId);
    if (form.parentDomainId) return all.filter((s) => s.parentId === form.parentDomainId);
    return all;
  }, [domainOptions, form.domainId, form.parentDomainId]);

  const filteredAssets = useMemo(() => {
    return assets.filter((a) => {
      const nameMatch = (a.asset_name || "").toLowerCase().includes(searchName.toLowerCase());
      const descMatch = (a.description || "").toLowerCase().includes(searchDesc.toLowerCase());
      return nameMatch && descMatch;
    });
  }, [assets, searchName, searchDesc]);

  useEffect(() => {
    if (form.parentDomainId && domainOptions.length === 1 && !form.domainId)
      setForm((f) => ({ ...f, domainId: domainOptions[0].id }));
  }, [domainOptions, form.parentDomainId, form.domainId]);

  useEffect(() => {
    if (form.domainId && subDomainOptions.length === 1 && !form.subDomainId)
      setForm((f) => ({ ...f, subDomainId: subDomainOptions[0].id }));
  }, [subDomainOptions, form.domainId, form.subDomainId]);

  const handleParentChange = (e) => {
    setForm((f) => ({ ...f, parentDomainId: e.target.value, domainId: "", subDomainId: "" }));
    setErrors((er) => ({ ...er, parentDomainId: "" }));
  };
  const handleDomainChange = (e) => {
    const id = e.target.value;
    const domain = domainOptions.find((d) => d.id === id);
    setForm((f) => ({ ...f, domainId: id, subDomainId: "", parentDomainId: domain?.parentId || f.parentDomainId }));
    setErrors((er) => ({ ...er, domainId: "" }));
  };
  const handleSubDomainChange = (e) => {
    const id = e.target.value;
    const sub = subDomainOptions.find((s) => s.id === id);
    setForm((f) => ({ ...f, subDomainId: id, domainId: sub?.domainId || f.domainId, parentDomainId: sub?.parentId || f.parentDomainId }));
    setErrors((er) => ({ ...er, subDomainId: "" }));
  };

  useEffect(() => {
    if (!open || !productId) return;
    const fetchAssets = async () => {
      setLoadingAssets(true);
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/assets-summary/${productId}`);
        setAssets(data || []);
        setOriginalAssets(data || []);
      } catch (error) {
        console.error("Failed to load assets", error);
        setAssets([]);
        setOriginalAssets([]);
      } finally {
        setLoadingAssets(false);
      }
    };
    fetchAssets();
  }, [open, productId]);

  const allFilled = form.productName.trim() && form.parentDomainId && form.domainId && form.subDomainId;

  const handleSubmit = async () => {
    const e = {};
    if (!form.productName.trim()) e.productName = "Product name is required.";
    if (!form.parentDomainId) e.parentDomainId = "Please select a parent domain.";
    if (!form.domainId) e.domainId = "Please select a domain.";
    if (!form.subDomainId) e.subDomainId = "Please select a sub domain.";
    if (Object.keys(e).length) { setErrors(e); return; }

    setSubmitting(true);
    try {
      const payload = {
        product_name: form.productName.trim(),
        parent_domain_id: form.parentDomainId,
        domain_id: form.domainId,
        sub_domain_id: form.subDomainId,
      };
      const { data } = await axios.put(`${process.env.REACT_APP_API_URL}/update-product/${productId}`, payload);
      onSuccess?.(data);
      onClose();
    } catch {
      setErrors({ submit: "Something went wrong. Please try again." });
      onFailed?.();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAssets = async () => {
    const changed = assets.filter((asset) => {
      const original = originalAssets.find((o) => o.asset_id === asset.asset_id);
      return original && original.description !== asset.description;
    });

    if (!changed.length) {
      setEditMode(false);
      return;
    }

    setSavingAssets(true);
    try {
      const payload = {
        type: "asset",
        items: changed.map((a) => ({
          id: a.asset_id,
          new_description: a.description,
        })),
      };
      await axios.put(`${process.env.REACT_APP_API_URL}/description`, payload);
      setOriginalAssets([...assets]);
      setEditMode(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to save descriptions", error);
    } finally {
      setSavingAssets(false);
    }
  };

  if (!open) return null;

  const inputBase = "w-full px-3 py-2.5 text-sm rounded-lg border bg-gray-50 text-gray-800 outline-none transition-all duration-150 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 focus:bg-white appearance-none";
  const inputNormal = "border-gray-200";
  const inputErr = "border-red-400 focus:ring-red-400 focus:border-red-400";
  const inputDis = "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200 pointer-events-none";
  const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5";
  const errorCls = "mt-1 text-xs text-red-500 flex items-center gap-1";
  const selCls = (hasErr, dis) => `${inputBase} pr-8 ${dis ? inputDis : hasErr ? inputErr : inputNormal}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(60,50,120,0.18)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-[900px] bg-white rounded-2xl shadow-2xl h-[500px] flex flex-col overflow-hidden"
        style={{ animation: "epIn .22s cubic-bezier(.34,1.56,.64,1)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 bg-[#6C5CE7] flex-shrink-0">
          <div>
            <h2 className="text-white font-bold text-lg tracking-tight">Edit Product</h2>
            <p className="text-violet-200 text-xs mt-0.5">Update product details below</p>
          </div>
          <button onClick={onClose} className="h-8 rounded-lg flex items-center justify-center text-white text-sm hover:opacity-70 transition-opacity">
            <X className="h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50 sticky top-0 z-10 flex-shrink-0">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${activeTab === tab.id
                ? "border-b-2 border-violet-600 text-violet-600 bg-white"
                : "border-b-2 border-transparent text-gray-400 hover:text-gray-600"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Product details */}
        {activeTab === "details" && (
          <div className="flex-1 overflow-y-auto">
            <div className="px-6 pt-6 pb-4 flex flex-col gap-5">
              <div>
                <label className={labelCls}>Product Name <span className="text-red-400">*</span></label>
                <input
                  className={`${inputBase} ${errors.productName ? inputErr : inputNormal}`}
                  placeholder="Enter product name"
                  value={form.productName}
                  onChange={(e) => { setForm((f) => ({ ...f, productName: e.target.value })); setErrors((er) => ({ ...er, productName: "" })); }}
                />
                {errors.productName && <p className={errorCls}><span>⚠</span>{errors.productName}</p>}
              </div>

              <div>
                <label className={labelCls}>Parent Domain <span className="text-red-400">*</span></label>
                <div className="relative">
                  <select className={selCls(!!errors.parentDomainId, loadingHierarchy)} value={form.parentDomainId} onChange={handleParentChange} disabled={loadingHierarchy}>
                    <option value="">{loadingHierarchy ? "Loading…" : "Select parent domain"}</option>
                    {parentOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-violet-500 text-xs">▼</span>
                </div>
                {errors.parentDomainId && <p className={errorCls}><span>⚠</span>{errors.parentDomainId}</p>}
              </div>

              <div>
                <label className={labelCls}>Domain <span className="text-red-400">*</span></label>
                <div className="relative">
                  <select className={selCls(!!errors.domainId, loadingHierarchy || !domainOptions.length)} value={form.domainId} onChange={handleDomainChange} disabled={loadingHierarchy || !domainOptions.length}>
                    <option value="">
                      {loadingHierarchy ? "Loading…" : form.parentDomainId && !domainOptions.length ? "No domains available" : "Select domain"}
                    </option>
                    {domainOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-violet-500 text-xs">▼</span>
                </div>
                {errors.domainId && <p className={errorCls}><span>⚠</span>{errors.domainId}</p>}
              </div>

              <div>
                <label className={labelCls}>Sub Domain <span className="text-red-400">*</span></label>
                <div className="relative">
                  <select className={selCls(!!errors.subDomainId, loadingHierarchy || !subDomainOptions.length)} value={form.subDomainId} onChange={handleSubDomainChange} disabled={loadingHierarchy || !subDomainOptions.length}>
                    <option value="">
                      {loadingHierarchy ? "Loading…" : form.domainId && !subDomainOptions.length ? "No sub domains available" : "Select sub domain"}
                    </option>
                    {subDomainOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-violet-500 text-xs">▼</span>
                </div>
                {errors.subDomainId && <p className={errorCls}><span>⚠</span>{errors.subDomainId}</p>}
              </div>

              {errors.submit && <p className="text-center text-sm text-red-500">⚠ {errors.submit}</p>}

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={onClose} className="px-5 py-2.5 rounded-lg text-sm font-semibold text-gray-500 border border-gray-200 hover:bg-violet-50 hover:text-violet-600 hover:border-violet-200 transition-all duration-150">
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!allFilled || submitting}
                  className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 flex items-center gap-2 ${allFilled && !submitting ? "bg-[#6C5CE7] text-white" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
                >
                  {submitting ? <><Loader2 className="animate-spin h-4 w-4" />Saving…</> : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Asset info */}
        {activeTab === "info" && (
          <div className="flex-1 overflow-hidden flex flex-col px-6 py-3">

            {/* Edit / Cancel / Save row */}
            <div className="flex justify-end gap-2 py-3 flex-shrink-0">
              {editMode && (
                <button
                  onClick={() => { setAssets([...originalAssets]); setEditMode(false); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => editMode ? handleSaveAssets() : setEditMode(true)}
                disabled={savingAssets}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-[#6366F1] text-white rounded-lg disabled:opacity-60 transition-colors hover:bg-[#4F46E5]"
              >
                {savingAssets ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" />Saving…</>
                ) : (
                  <><Pencil className="h-3.5 w-3.5" />{editMode ? "Save" : "Edit"}</>
                )}
              </button>
            </div>

            {loadingAssets ? (
              <div className="flex items-center justify-center py-10">
                <div className="text-center">
                  <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
                  <p className="text-gray-600 text-lg">Loading product details...</p>
                </div>
              </div>
            ) : assets.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-10">No assets found.</p>
            ) : (

              <div ref={tableWrapperRef} className="flex-1 overflow-y-auto">
                <div className="relative">                <div
                  style={{ left: `${colWidth}%`, top: 0 }}
                  className="absolute top-0 bottom-0 w-[10px] z-20 cursor-col-resize group -translate-x-1/2 pointer-events-auto min-h-full"
                  onMouseDown={onDragMouseDown}
                >
                  <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[3px] bg-[#bb1ccc] group-hover:bg-[#bb1ccc] transition-colors duration-150" />
                </div>

                  <table className="w-full text-sm border-collapse table-fixed">
                    <colgroup>
                      <col style={{ width: `${colWidth}%` }} />
                      <col style={{ width: `${100 - colWidth}%` }} />
                    </colgroup>

                    <thead className="sticky top-0 z-10 bg-gray-50">
                      <tr>
                        <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide select-none">
                          Asset Name
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide select-none">
                          Description
                        </th>
                      </tr>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 border-b border-gray-200 font-normal">
                          <input
                            type="text"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            placeholder="Search asset name…"
                            className="w-full px-2 py-1.5 text-xs rounded-md border border-gray-200 bg-white text-gray-800 outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400"
                          />
                        </th>
                        <th className="px-3 py-2 border-b border-gray-200 font-normal">
                          <input
                            type="text"
                            value={searchDesc}
                            onChange={(e) => setSearchDesc(e.target.value)}
                            placeholder="Search description…"
                            className="w-full px-2 py-1.5 text-xs rounded-md border border-gray-200 bg-white text-gray-800 outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400"
                          />
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredAssets.length === 0 ? (
                        <tr>
                          <td colSpan={2} className="text-center text-sm text-gray-400 py-10">
                            No matching assets found.
                          </td>
                        </tr>
                      ) : (
                        filteredAssets.map((asset) => (
                          <tr key={asset.asset_id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 align-top">
                            <td className="py-3 px-3 font-medium text-gray-800 overflow-hidden">
                              <div className="truncate" title={asset.asset_name}>
                                {asset.asset_name}
                              </div>
                            </td>
                            <td className="py-3 px-3 text-gray-600 overflow-hidden">
                              {editMode ? (
                                <textarea
                                  className="w-full border border-[#6366F1] rounded-lg px-3 py-2 text-sm text-black outline-none focus:ring-1 focus:ring-[#6366F1] resize-y min-h-[60px]"
                                  value={asset.description === "N/A" ? "" : (asset.description || "")}
                                  placeholder="Enter description…"
                                  rows={2}
                                  onChange={(e) =>
                                    setAssets((prev) =>
                                      prev.map((a) =>
                                        a.asset_id === asset.asset_id ? { ...a, description: e.target.value } : a
                                      )
                                    )
                                  }
                                />
                              ) : (
                                <div className="max-h-[60px] overflow-y-auto text-sm leading-relaxed">
                                  {asset.description === "N/A" || !asset.description ? "—" : asset.description}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`
        @keyframes epIn {
          from { opacity: 0; transform: scale(.93) translateY(14px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default EditProductModal;